import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import { DT_FORM_FIELDS, DT_PROCESS_TYPES, PATHS } from '../../constants';
import {
  resolveUserIdsForReportApi,
  serializeUsersSelectedForUrl,
} from '../../utils/usersSelectedParams';

/**
 * ReportDtManager class is responsible for generating reports based on provided filters
 * @class
 * @param {Object} filters - Filters object
 * @param {String} processType - Process type, either 'individual or 'group' or 'hash'
 * @param {Object} body - Request body, contain data to consume API
 */
class ReportDtManager {
  constructor(filters, processType, body) {
    this.filters = filters;
    this.body = body;
    this.processType = processType;
    this.getRangeDate = this.getRangeDate.bind(this);
    this.buildRequestReportParams = this.buildRequestReportParams.bind(this);
  }

  /**
   *
   * @returns {String} Query params string
   */
  buildQueryParams() {
    try {
      const currentPath = window.location.pathname;
      const safeFilters = this.filters || {};
      const dates = {
        startDate: safeFilters.startDate?.valueOf?.() ?? null,
        endDate: safeFilters.endDate?.valueOf?.() ?? null,
        period: safeFilters.period ?? null,
      };
      const standardFilters = {
        ...dates,
        processType: this.processType,
      };

      const paramsByProcessType = {
        [DT_PROCESS_TYPES.INDIVIDUAL]: {
          ...standardFilters,
          ...(safeFilters[DT_FORM_FIELDS.USER_ID] && {
            userId: safeFilters[DT_FORM_FIELDS.USER_ID],
          }),
        },
        [DT_PROCESS_TYPES.GROUP]: {
          ...standardFilters,
          [DT_FORM_FIELDS.WORKING_DAY]: safeFilters[DT_FORM_FIELDS.WORKING_DAY] ?? null,
          [DT_FORM_FIELDS.SHIFT]: safeFilters[DT_FORM_FIELDS.SHIFT] ?? null,
          [DT_FORM_FIELDS.REGION]: safeFilters[DT_FORM_FIELDS.REGION] ?? null,
          [DT_FORM_FIELDS.PREMISES]: safeFilters[DT_FORM_FIELDS.PREMISES] ?? null,
          [DT_FORM_FIELDS.JOB]: safeFilters[DT_FORM_FIELDS.JOB] ?? null,
          [DT_FORM_FIELDS.CONSTRACTOR]: safeFilters[DT_FORM_FIELDS.CONSTRACTOR] ?? null,
          [DT_FORM_FIELDS.USERS_SELECTED]: serializeUsersSelectedForUrl(
            safeFilters[DT_FORM_FIELDS.USERS_SELECTED],
          ),
        },
        [DT_PROCESS_TYPES.ISSUES]: {
          ...standardFilters,
        },
      };

      const params = paramsByProcessType[this.processType] || standardFilters;
      const query = new URLSearchParams(params).toString();
      return `${currentPath}?${query}`;
    } catch (error) {
      console.error('Error building query params:', error);
      return window.location.pathname;
    }
  }

  async callCloudFunction(op, params) {
    if (!op) {
      return {
        success: false,
        error: {
          code: 'invalid-function-name',
          message: 'Function name is required',
        },
      };
    }
    try {
      const httpRef = apiCallable(op);
      const result = await httpRef(params);
      return {
        success: true,
        data: result?.data,
      };
    } catch (error) {
      const normalizedError = {
        code: error?.code || 'unknown',
        message: error?.message || 'Unknown error',
        details: error?.details || null,
      };
      console.error(`Error calling API operation ${op}:`, normalizedError);
      return {
        success: false,
        error: normalizedError,
      };
    }
  }

  getRangeDate() {
    const { startDate, endDate, period } = this.body || {};

    // Period takes precedence
    if (period !== undefined && period !== null && period !== '' && period !== 'undefined' && period !== 'null') {
      const now = new Date();
      const days = Number(period);
      if (!Number.isNaN(days)) {
        const lte = new Date(now);
        lte.setHours(23, 59, 59, 999); // End of current day
        const gte = new Date(now);
        gte.setDate(gte.getDate() - days);
        gte.setHours(0, 0, 0, 0); // Start of day
        return { gte, lte };
      }
    }

    const startMs = Number(startDate);
    const endMs = Number(endDate);
    let gte = Number.isFinite(startMs) ? new Date(startMs) : null;
    let lte = Number.isFinite(endMs) ? new Date(endMs) : null;

    // Set start date to beginning of day (00:00:00)
    if (gte) {
      gte.setHours(0, 0, 0, 0);
    }

    // Set end date to end of day (23:59:59)
    if (lte) {
      lte.setHours(23, 59, 59, 999);
    }

    const isValid = d => d instanceof Date && !Number.isNaN(d.valueOf());

    if (isValid(gte) || isValid(lte)) {
      return { gte, lte };
    }

    return null;
  }

  buildRequestReportParams(page) {
    const body = this.body || {};
    const userIds = resolveUserIdsForReportApi({
      usersSelected: body.usersSelected,
      userId: body.userId,
      processType: this.processType,
    });

    const periodRange = this.getRangeDate();
    let range = null;
    if (periodRange) {
      const gteMs = periodRange.gte?.valueOf?.();
      const lteMs = periodRange.lte?.valueOf?.();
      if (Number.isFinite(gteMs) || Number.isFinite(lteMs)) {
        range = { gte: gteMs, lte: lteMs };
      }
    }

    const commonParams = {
      organizationIds: body.orgIds,
      userIds,
      range, // when null, backend should return all data
      lastPosition: Number.isFinite(Number(page)) ? Number(page) : 0,
    };

    switch (this.body.reportType) {
      case PATHS.ATTENDANCE:
        return commonParams;
      case PATHS.HOLIDAYS:
        return commonParams;
      case PATHS.DAILY:
        return commonParams;
      case PATHS.ISSUES:
        return commonParams;
      case PATHS.MODIFICATIONS:
        return commonParams;
      default:
        return commonParams;
    }
  }

  async generateReport(page = 0) {
    try {
      const API_OPERATIONS_BY_REPORT_PATH = {
        [PATHS.ATTENDANCE]: API_OPERATIONS.SHIFT_REPORT_DT,
        [PATHS.HOLIDAYS]: API_OPERATIONS.HOLIDAYS_REPORT_DT,
        [PATHS.DAILY]: API_OPERATIONS.DAILY_REPORT_DT,
        [PATHS.ISSUES]: API_OPERATIONS.INCIDENTS_REPORT_DT,
        [PATHS.MODIFICATIONS]: API_OPERATIONS.MODIFICATIONS_REPORT_DT,
      };

      const op = API_OPERATIONS_BY_REPORT_PATH?.[this.body?.reportType];
      if (!op) {
        return {
          success: false,
          error: {
            code: 'invalid-report-type',
            message: `Unsupported report type: ${this.body?.reportType}`,
          },
        };
      }

      const params = this.buildRequestReportParams(page);
      const res = await this.callCloudFunction(op, params);

      if (!res.success) {
        return {
          success: false,
          error: res.error,
        };
      }

      return {
        success: true,
        data: res.data,
      };
    } catch (error) {
      console.error('Error generating report:', error);
      return {
        success: false,
        error: {
          code: 'generate-report-failed',
          message: error?.message || 'Failed to generate report',
        },
      };
    }
  }
}

export default ReportDtManager;
